import { useCallback, useEffect, useState } from 'react';

import elementClosest from '@rover/react-lib/src/utils/elementClosest';

import { SCROLL_AREA_CLASSNAME } from './constants';

// with some tweaking, this could be a reusable utility

// styles are defined in app/css/body-scroll-prevention.scss & shared/js/styles/globalClasses.ts
const CLASS_NAME = 'prevent-body-scroll';

/**
 * This is necessary so that the notifications center icon is visible
 * at the top. A CSS alternative, setting the body's position to `fixed`
 * would not work because the mobile banner will push the navbar
 * below the top of the notifications center.
 *
 * It's safe to scroll to the top of the page here because the notifications
 * center icon is there and you'd need to have basically scrolled to the top
 * of the page. However, if the banner is open and you click the notifications
 * center icon then it is necessary for us to make sure that the notifications
 * center icon is in view and not the banner.
 */
const scrollToTopOfBody = (): void => {
  const { body } = document;
  if (body) {
    const scrollTop = body.classList.contains('has-banner') ? 70 : 0;
    body.scrollTop = scrollTop;
  }
};

const preventBodyScroll = (rawEvent: TouchEvent): void => {
  const e = rawEvent || window.event;
  const { target } = e;

  if (target instanceof Element) {
    // Allow scrolling within the notifications dropdown
    if (elementClosest(target, `.${SCROLL_AREA_CLASSNAME}`)) return;
  }

  if (e.preventDefault) e.preventDefault();
};

const eventListenerOptions: AddEventListenerOptions & EventListenerOptions = {
  passive: false,
};

const addTouchMoveListener = (): void => {
  document.body.addEventListener('touchmove', preventBodyScroll, eventListenerOptions);
};

const removeTouchMoveListener = (): void => {
  document.body.removeEventListener('touchmove', preventBodyScroll, eventListenerOptions);
};

const usePreventBodyScroll = (): ((setting?: boolean) => void) => {
  const [shouldPreventBodyScroll, setShouldPreventBodyScroll] = useState(false);

  // Reacts to changes made to shouldPreventBodyScroll
  useEffect(() => {
    if (shouldPreventBodyScroll) {
      document.body.classList.add(CLASS_NAME);
      addTouchMoveListener();
      scrollToTopOfBody();
    } else {
      document.body.classList.remove(CLASS_NAME);
      removeTouchMoveListener();
    }
    // return cleanup func
    return removeTouchMoveListener;
  }, [shouldPreventBodyScroll]);

  const togglePreventBodyScroll = useCallback((value?: boolean): void => {
    setShouldPreventBodyScroll((currentValue) => {
      return value ?? !currentValue;
    });
  }, []);

  return togglePreventBodyScroll;
};

export default usePreventBodyScroll;
